import {
  PASSWORD_STRENGTH_FILLED_SEGMENTS,
  PASSWORD_STRENGTH_LABEL_KEYS,
  PASSWORD_STRENGTH_SEGMENT_COUNT,
} from "@frontend-shared/constants";
import { EPasswordStrength, getPasswordStrength } from "@frontend-shared/utils";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  PASSWORD_STRENGTH_LABEL_CLASS_NAMES,
  PASSWORD_STRENGTH_SEGMENT_CLASS_NAMES,
  PASSWORD_STRENGTH_SEGMENT_INDEXES,
} from "@/components/constants/password-strength-meter";
import { cn } from "@/lib/utils";

import type { IPasswordStrengthMeterProps } from "./PasswordStrengthMeter.types";

// NOTE: NSAI-5324 — advisory only. The scoring is shared with mobile through
// `getPasswordStrength`; `passwordSchema` remains the only gate, so when a
// weak password still meets the policy the hint says it can be submitted.
const PasswordStrengthMeterComponent = ({
  password,
  "data-testid": testId,
  className,
}: IPasswordStrengthMeterProps): JSX.Element | null => {
  const { t } = useTranslation("auth");
  const strength = useMemo(() => getPasswordStrength({ password }), [password]);

  if (!password) return null;

  const filledSegments = PASSWORD_STRENGTH_FILLED_SEGMENTS[strength.level];
  const isWeakButAllowed =
    strength.level === EPasswordStrength.WEAK && strength.isPolicyMet;

  return (
    <div
      className={cn("flex flex-col gap-1.5", className)}
      data-testid={testId}
      data-strength={strength.level}
    >
      <meter
        className="sr-only"
        aria-label={t("password_strength.label")}
        aria-valuetext={t(PASSWORD_STRENGTH_LABEL_KEYS[strength.level])}
        min={0}
        max={PASSWORD_STRENGTH_SEGMENT_COUNT}
        value={filledSegments}
      />
      {/* NOTE: the native <meter> above carries the value for assistive tech;
          the segments are its visual twin, so they stay out of the tree. */}
      <div className="flex gap-1" aria-hidden="true">
        {PASSWORD_STRENGTH_SEGMENT_INDEXES.map((index) => (
          <span
            key={index}
            className={cn(
              "h-1 flex-1 rounded-full bg-border transition-colors",
              index < filledSegments &&
                PASSWORD_STRENGTH_SEGMENT_CLASS_NAMES[strength.level],
            )}
          />
        ))}
      </div>
      <p
        className={cn(
          "text-xs font-medium",
          PASSWORD_STRENGTH_LABEL_CLASS_NAMES[strength.level],
        )}
        aria-live="polite"
        data-testid={`${testId}-label`}
      >
        {t(PASSWORD_STRENGTH_LABEL_KEYS[strength.level])}
      </p>
      {isWeakButAllowed && (
        <p
          className="text-xs text-foreground-secondary"
          data-testid={`${testId}-hint`}
        >
          {t("password_strength.weak_hint")}
        </p>
      )}
    </div>
  );
};

export const PasswordStrengthMeter = memo(PasswordStrengthMeterComponent);
